import React, {
  useEffect, useMemo, useReducer, useRef, useState, useCallback,
} from "react";
import { createPortal } from "react-dom";
import { City, Country, State } from "country-state-city";
import {
  X, UserPlus, AlertCircle, Phone, Mail, ChevronDown,
  Briefcase, MapPin, DollarSign, FileText, Users,
  CheckCircle2, Tag, Plus, Trash2,
} from "lucide-react";

/* ─── Constants ───────────────────────────────────────────── */

const API_BASE = "https://candi.vdpl.co/api/index.php/";

const TZ_BY_STATE = {
  US: {
    AL:"America/Chicago",AK:"America/Anchorage",AZ:"America/Phoenix",AR:"America/Chicago",
    CA:"America/Los_Angeles",CO:"America/Denver",CT:"America/New_York",DE:"America/New_York",
    FL:"America/New_York",GA:"America/New_York",HI:"Pacific/Honolulu",ID:"America/Denver",
    IL:"America/Chicago",IN:"America/Indiana/Indianapolis",KS:"America/Chicago",
    KY:"America/New_York",LA:"America/Chicago",MA:"America/New_York",MD:"America/New_York",
    ME:"America/New_York",MI:"America/Detroit",MN:"America/Chicago",MO:"America/Chicago",
    MS:"America/Chicago",MT:"America/Denver",NC:"America/New_York",ND:"America/Chicago",
    NE:"America/Chicago",NH:"America/New_York",NJ:"America/New_York",NM:"America/Denver",
    NV:"America/Los_Angeles",NY:"America/New_York",OH:"America/New_York",OK:"America/Chicago",
    OR:"America/Los_Angeles",PA:"America/New_York",RI:"America/New_York",SC:"America/New_York",
    SD:"America/Chicago",TN:"America/Chicago",TX:"America/Chicago",UT:"America/Denver",
    VA:"America/New_York",VT:"America/New_York",WA:"America/Los_Angeles",WI:"America/Chicago",
    WV:"America/New_York",WY:"America/Denver",DC:"America/New_York",PR:"America/Puerto_Rico",
  },
  CA: {
    AB:"America/Edmonton",BC:"America/Vancouver",MB:"America/Winnipeg",NB:"America/Moncton",
    NL:"America/St_Johns",NS:"America/Halifax",NT:"America/Yellowknife",NU:"America/Iqaluit",
    ON:"America/Toronto",PE:"America/Halifax",QC:"America/Toronto",SK:"America/Regina",
    YT:"America/Whitehorse",
  },
  AU: {
    NSW:"Australia/Sydney",VIC:"Australia/Melbourne",QLD:"Australia/Brisbane",
    SA:"Australia/Adelaide",WA:"Australia/Perth",TAS:"Australia/Hobart",
    NT:"Australia/Darwin",ACT:"Australia/Sydney",
  },
};

/* ─── Pure helpers ────────────────────────────────────────── */

const norm = (v) => String(v ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const parseSkills = (v) => {
  if (!v) return [];
  if (Array.isArray(v)) return v.map(String).filter(Boolean);
  return String(v).split(",").map((s) => s.trim()).filter(Boolean);
};

const joinSkills = (arr) => arr.join(", ");

const toPhoneCode = (raw) => {
  const s = String(raw ?? "").trim();
  if (!s) return "+1";
  return s.startsWith("+") ? s : `+${s}`;
};

/** Split "rawPhone" into { code, number }, respecting explicit code. */
const splitPhone = (rawPhone, explicitCode) => {
  const phone = String(rawPhone ?? "").trim();
  const code = toPhoneCode(explicitCode);
  if (!phone) return { code, number: "" };
  if (/^\+\d+$/.test(phone)) return { code: phone, number: "" };
  const m = phone.match(/^(\+\d+)\s+(.+)$/);
  if (m) return { code: m[1].trim(), number: m[2].trim() };
  if (phone.startsWith(code)) return { code, number: phone.slice(code.length).trim() };
  return { code, number: phone };
};

const fileNameFromUrl = (url) => {
  const s = String(url ?? "").split("?")[0].split("#")[0];
  const parts = s.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : s;
};

const normalizeDocumentUrl = (rawUrl) => {
  const value = String(rawUrl ?? "").trim();
  if (!value) return "";
  const origin = "https://candi.vdpl.co";

  try {
    const parsed = new URL(value, origin);
    const path = parsed.pathname || "";
    if (path.startsWith("/api/")) {
      return `${origin}${path}${parsed.search}${parsed.hash}`;
    }
    if (path.startsWith("/uploads/")) {
      return `${origin}/api${path}${parsed.search}${parsed.hash}`;
    }
    return parsed.href;
  } catch {
    const withSlash = value.startsWith("/") ? value : `/${value}`;
    const apiIndex = withSlash.toLowerCase().indexOf("/api/");
    if (apiIndex >= 0) return `${origin}${withSlash.slice(apiIndex)}`;
    if (withSlash.startsWith("/uploads/")) return `${origin}/api${withSlash}`;
    return value;
  }
};

const yesNo = (v, fallback = "No") => {
  const s = norm(v);
  if (["yes","y","1","true"].includes(s)) return "Yes";
  if (["no","n","0","false"].includes(s)) return "No";
  return fallback;
};

const retryAsync = async (op, retries = 2, delay = 700) => {
  for (let i = 0; i <= retries; i++) {
    try { return await op(); }
    catch (e) {
      const isAbort = e?.name === "AbortError";
      const status = Number(e?.status ?? 0);
      if (isAbort || i === retries || (status >= 400 && status < 500)) throw e;
      await new Promise((r) => setTimeout(r, delay * (i + 1)));
    }
  }
};

/* ─── Form initializer ────────────────────────────────────── */

const FORM_DEFAULTS = {
  name: "", title: "", emailId: "", secondaryEmailId: "",
  linkedinUrl: "", phoneCountryCode: "+1", phoneNumber: "",
  secondaryPhoneCountryCode: "+1", secondaryPhoneNumber: "",
  resumeFileName: "", resumeFileUrl: "", resumeFileDocId: "", resumeFile: null,
  visaFileDocs: [], visaFiles: [],
  workedWithVdart: "No", referredBy: "", referredById: "",
  previousClient: "", city: "", state: "", experience: "",
  timeZonePrimary: "", country: "",
  countryId: null, stateId: null, cityId: null,
  relocation: "No", workMode: ["Onsite"],
  expectedSalary: "", workAuthorization: "", workAuthorizationName: "",
  candidateConnect: "", candidateConnectVia: "",
  benchStatus: "", benchStatusName: "",
  candidateResponse: "", candidateReason: "", comments: "",
  primarySkills: "", secondarySkills: "",
};

const buildForm = (src, defaultTZ, defaultCountry) => {
  if (!src) return { ...FORM_DEFAULTS, timeZonePrimary: defaultTZ, country: defaultCountry };
  const info = { ...src, ...(src.candidateInfo ?? {}) };
  const primary = splitPhone(info.phoneNumber ?? info.candidate_phone, info.phoneCountryCode);
  const secondary = splitPhone(info.secondaryPhoneNumber ?? info.candidate_phone_secondary, info.secondaryPhoneCountryCode);
  const wm = (() => {
    const raw = info.workMode ?? info.work_mode;
    if (Array.isArray(raw)) return raw.filter(Boolean);
    if (raw) return String(raw).split(",").map((s) => s.trim()).filter(Boolean);
    return ["Onsite"];
  })();
  const refId = String(info.referredById ?? info.referred_by ?? "").trim();
  const workAuthId = String(info.workAuthorizationId ?? info.workAuthorization ?? info.work_authorization_id ?? "").trim();
  const benchId = String(info.benchStatus ?? info.bench_status_id ?? info.engagement_bench_status_id ?? "").trim();

  return {
    ...FORM_DEFAULTS,
    name:                     info.name                 ?? info.candidate_name           ?? "",
    title:                    info.title                ?? info.job_title                ?? "",
    emailId:                  info.emailId              ?? info.candidate_email           ?? "",
    secondaryEmailId:         info.secondaryEmailId     ?? info.candidate_email_secondary ?? "",
    linkedinUrl:              info.linkedinUrl          ?? info.linkedin_url              ?? info.linkedin ?? "",
    phoneCountryCode:         primary.code,
    phoneNumber:              primary.number,
    secondaryPhoneCountryCode: secondary.code,
    secondaryPhoneNumber:     secondary.number,
    resumeFileName:           info.resumeFileName       ?? info.cv_link                  ?? "",
    resumeFileUrl:            info.resumeFileUrl        ?? "",
    resumeFileDocId:          info.resumeFileDocId      ?? info.resume_doc_id            ?? "",
    visaFileDocs:             Array.isArray(info.visaFileDocs) ? info.visaFileDocs : [],
    workedWithVdart:          yesNo(info.workedWithVdart ?? info.worked_with_vdart),
    referredBy:               info.referredBy           ?? info.ref_name                 ?? "",
    referredById:             refId === "0" ? "" : refId,
    previousClient:           info.previousClient       ?? info.client                   ?? "",
    city:                     info.city                 ?? info.project_city             ?? "",
    state:                    info.state                ?? info.project_state            ?? "",
    experience:               info.experience           ?? info.experience_years          ?? "",
    timeZonePrimary:          info.timeZonePrimary      ?? info.timezone_primary          ?? defaultTZ ?? "",
    country:                  info.country              ?? info.project_country           ?? defaultCountry ?? "",
    countryId:                info.countryId            ?? null,
    stateId:                  info.stateId              ?? null,
    cityId:                   info.cityId               ?? null,
    relocation:               info.relocation           ?? "No",
    workMode:                 wm,
    expectedSalary:           info.expectedSalary       ?? info.expected_salary           ?? "",
    workAuthorization:        workAuthId,
    workAuthorizationName:    String(info.workAuthorizationName ?? info.work_authorization_name ?? "").trim(),
    candidateConnect:         info.candidateConnect     ?? info.candidate_connect         ?? "",
    candidateConnectVia:      info.candidateConnectVia  ?? info.candidate_connect_via    ?? info.connected_via ?? "",
    benchStatus:              benchId,
    benchStatusName:          String(info.benchStatusName ?? info.bench_status_name ?? "").trim(),
    candidateResponse:        info.candidateResponse    ?? info.candidate_response        ?? info.engagement_response ?? "",
    candidateReason:          info.candidateReason      ?? info.candidate_reason          ?? "",
    comments:                 info.comments             ?? info.rd_comments               ?? "",
    primarySkills:            info.primarySkills        ?? info.primary_skills            ?? "",
    secondarySkills:          info.secondarySkills      ?? info.secondary_skills          ?? "",
  };
};

/* ─── Form reducer ────────────────────────────────────────── */
// Replaces all scattered setForm(p => ({...p, key: val })) with typed actions.

const formReducer = (state, action) => {
  switch (action.type) {
    case "RESET":      return action.payload;
    case "SET_FIELD":  return { ...state, [action.key]: action.value };
    case "SET_FIELDS": return { ...state, ...action.payload };
    case "TOGGLE_WORK_MODE": {
      const modes = new Set(state.workMode ?? []);
      modes.has(action.mode) ? modes.delete(action.mode) : modes.add(action.mode);
      return { ...state, workMode: Array.from(modes) };
    }
    case "SET_COUNTRY": {
      const { country, countryId } = action;
      return { ...state, country, countryId, state: "", stateId: null, city: "", cityId: null };
    }
    case "SET_STATE": {
      const { stateName, stateId } = action;
      return { ...state, state: stateName, stateId, city: "", cityId: null };
    }
    case "SET_CITY":   return { ...state, city: action.city, cityId: action.cityId };
    case "ADD_VISA_FILES":
      return { ...state, visaFiles: [...(state.visaFiles ?? []), ...action.files] };
    case "REMOVE_VISA_FILE":
      return { ...state, visaFiles: state.visaFiles.filter((_, i) => i !== action.index) };
    default: return state;
  }
};

/* ─── Custom hook: useFetchList ───────────────────────────── */
// Single hook replacing 4 identical fetch-useEffect patterns.

const useFetchList = (url, mapFn, { enabled = true } = {}) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const mapRef = useRef(mapFn);

  useEffect(() => {
    mapRef.current = mapFn;
  }, [mapFn]);

  useEffect(() => {
    if (!enabled || !url) return;
    const ctrl = new AbortController();
    (async () => {
      setLoading(true); setError(""); setList([]);
      try {
        const res = await retryAsync(async () => {
          const r = await fetch(url, { signal: ctrl.signal });
          if (!r.ok) { const e = new Error(r.status); e.status = r.status; throw e; }
          return r;
        });
        const payload = await res.json();
        setList(mapRef.current(payload).filter(Boolean));
      } catch (e) {
        if (e?.name !== "AbortError") setError("Failed to load.");
      } finally { setLoading(false); }
    })();
    return () => ctrl.abort();
  }, [url, enabled]); // mapFn intentionally omitted — stable at call site

  return { list, loading, error, setList };
};

/* ─── Timezone resolver (pure, no side effects) ──────────── */

const resolveTimezone = (country, state, city) => {
  const tzs = country?.timezones ?? [];
  if (!tzs.length) return "";
  const override = TZ_BY_STATE?.[country?.isoCode]?.[state?.isoCode];
  if (override) {
    const m = tzs.find((t) => t?.zoneName === override);
    return m?.abbreviation ?? m?.zoneName ?? override;
  }
  if (tzs.length === 1) return tzs[0]?.abbreviation ?? tzs[0]?.zoneName ?? "";
  const lng = Number(city?.longitude ?? state?.longitude);
  if (Number.isFinite(lng)) {
    const est = (lng / 15) * 3600;
    const closest = [...tzs].sort((a, b) =>
      Math.abs(Number(a.gmtOffset) - est) - Math.abs(Number(b.gmtOffset) - est)
    )[0];
    return closest?.abbreviation ?? closest?.zoneName ?? "";
  }
  return tzs[0]?.abbreviation ?? tzs[0]?.zoneName ?? "";
};

/* ─── Reusable field components ──────────────────────────── */

const cls = {
  input: (err) =>
    `w-full rounded-xl border bg-white px-3.5 py-2.5 text-sm text-slate-800 placeholder-slate-400 outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 ${
      err ? "border-red-300 bg-red-50/30" : "border-slate-200 hover:border-slate-300"
    }`,
  select: (err, disabled) =>
    `w-full appearance-none rounded-xl border bg-white px-3.5 py-2.5 pr-9 text-sm text-slate-800 outline-none transition-all focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed ${
      err ? "border-red-300 bg-red-50/30" : "border-slate-200 hover:border-slate-300"
    } ${disabled ? "" : "cursor-pointer"}`,
};

const Field = ({ label, required, error, hint, action, children }) => (
  <div className="space-y-1.5">
    {(label || action) && (
      <div className="flex items-center justify-between">
        {label && (
          <label className="text-xs font-semibold text-slate-600 leading-none">
            {label}{required && <span className="ml-0.5 text-red-500">*</span>}
            {hint && <span className="ml-1.5 font-normal text-slate-400">{hint}</span>}
          </label>
        )}
        {action}
      </div>
    )}
    {children}
    {error && (
      <p className="flex items-center gap-1 text-[11px] text-red-600">
        <AlertCircle className="h-3 w-3 shrink-0" />{error}
      </p>
    )}
  </div>
);

/** Wrapper that appends the ChevronDown overlay — avoids repeating it 8× */
const SelectField = ({ label, required, error, hint, children }) => (
  <Field label={label} required={required} error={error} hint={hint}>
    <div className="relative">{children}
      <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
    </div>
  </Field>
);

const Section = ({ icon: Icon, dot, label, children, badge }) => (
  <div className="rounded-2xl border border-slate-200/80 bg-white overflow-hidden shadow-sm">
    <div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-gradient-to-r from-slate-50/80 to-white px-4 py-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className={`h-3.5 w-3.5 ${dot}`} strokeWidth={2.5} />}
        <h5 className="text-[11px] font-bold uppercase tracking-widest text-slate-500">{label}</h5>
      </div>
      {badge && <span className="rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-600">{badge}</span>}
    </div>
    <div className="grid gap-3 p-4">{children}</div>
  </div>
);

/* ─── Skill tag input ─────────────────────────────────────── */

const SKILL_COLORS = {
  blue: {
    tag: "bg-blue-50 border-blue-200 text-blue-700",
    rm: "text-blue-400 hover:text-blue-700 hover:bg-blue-100",
    add: "bg-blue-600 hover:bg-blue-700 text-white",
    ring: "focus:ring-blue-500/20 focus:border-blue-400",
  },
  emerald: {
    tag: "bg-emerald-50 border-emerald-200 text-emerald-700",
    rm: "text-emerald-400 hover:text-emerald-700 hover:bg-emerald-100",
    add: "bg-emerald-600 hover:bg-emerald-700 text-white",
    ring: "focus:ring-emerald-500/20 focus:border-emerald-400",
  },
};

const SkillTagInput = ({ value, onChange, placeholder, color = "blue" }) => {
  const [input, setInput] = useState("");
  const ref = useRef(null);
  const c = SKILL_COLORS[color] ?? SKILL_COLORS.blue;
  const skills = useMemo(() => parseSkills(value), [value]);

  const commit = useCallback(() => {
    const items = input.trim().split(/[,\n]+/).map((s) => s.trim()).filter(Boolean);
    if (!items.length) return;
    onChange(joinSkills([...new Set([...skills, ...items])]));
    setInput("");
    ref.current?.focus();
  }, [input, skills, onChange]);

  const remove = useCallback((s) => onChange(joinSkills(skills.filter((x) => x !== s))), [skills, onChange]);

  return (
    <div className="space-y-2">
      {skills.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {skills.map((s) => (
            <span key={s} className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-semibold ${c.tag}`}>
              {s}
              <button type="button" onClick={() => remove(s)}
                className={`flex h-3.5 w-3.5 items-center justify-center rounded-full transition-colors ${c.rm}`}>
                <X className="h-2.5 w-2.5" />
              </button>
            </span>
          ))}
          <button type="button" onClick={() => onChange("")}
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2 py-1 text-[11px] text-slate-400 hover:border-red-200 hover:text-red-500 transition-colors">
            <Trash2 className="h-2.5 w-2.5" />Clear all
          </button>
        </div>
      )}
      <div className="flex gap-2">
        <input ref={ref} type="text" value={input} placeholder={placeholder}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") { e.preventDefault(); commit(); }
            if (e.key === "Backspace" && !input && skills.length) remove(skills[skills.length - 1]);
          }}
          className={`flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm placeholder-slate-400 outline-none focus:ring-2 ${c.ring} transition-all`} />
        <button type="button" onClick={commit} disabled={!input.trim()}
          className={`flex items-center gap-1.5 rounded-xl px-3 py-2.5 text-xs font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed ${c.add}`}>
          <Plus className="h-3.5 w-3.5" />Add
        </button>
      </div>
      <p className="text-[10px] text-slate-400">Press Enter or comma to add · Backspace to remove last</p>
    </div>
  );
};

/* ─── Doc row ─────────────────────────────────────────────── */

const DocRow = ({ name, url, isStaged, onRemove, onDelete, deleteDisabled }) => (
  <div className={`flex items-start justify-between gap-2 rounded-xl border px-3 py-2 ${isStaged ? "border-blue-200 bg-blue-50/60" : "border-slate-200 bg-slate-50"}`}>
    <div className="flex min-w-0 flex-1 items-start gap-2">
      <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${isStaged ? "bg-blue-100" : "bg-emerald-100"}`}>
        <FileText className={`h-3.5 w-3.5 ${isStaged ? "text-blue-600" : "text-emerald-600"}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className={`break-all whitespace-normal text-[11px] font-semibold leading-4 ${isStaged ? "text-blue-700" : "text-slate-700"}`}>{name}</p>
        {isStaged && url instanceof File && (
          <p className="text-[10px] text-blue-400">{(url.size / 1024).toFixed(1)} KB · Pending upload</p>
        )}
      </div>
    </div>
    <div className="flex items-center gap-1.5 shrink-0">
      {!isStaged && url && (
        <a href={url} download target="_blank" rel="noopener noreferrer"
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
          </svg>
        </a>
      )}
      {!isStaged && onDelete && (
        <button type="button" onClick={onDelete} disabled={deleteDisabled}
          title="Delete document"
          className="flex h-7 w-7 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-500 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      {isStaged && onRemove && (
        <button type="button" onClick={onRemove}
          className="flex h-6 w-6 items-center justify-center rounded-lg text-blue-400 hover:bg-blue-200 hover:text-red-500 transition-colors">
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  </div>
);

/* ─── Engagement sub-section ─────────────────────────────── */

const EngagementSection = ({ form, dispatch, errors, benchStatusLoading, benchStatusError, visibleBenchStatuses }) => {
  const connectVal = norm(form.candidateConnect);
  return (
    <Section icon={Users} dot="text-blue-500" label="Candidate Engagement">
      <SelectField label="Candidate Connect" error={errors.candidateConnect}>
        <select value={form.candidateConnect}
          onChange={(e) => dispatch({ type: "SET_FIELD", key: "candidateConnect", value: e.target.value })}
          className={cls.select(errors.candidateConnect)}>
          <option value="">Select</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </SelectField>

      {["yes","no"].includes(connectVal) && (
        <Field label="Connected Via" required error={errors.candidateConnectVia}>
          <div className="grid grid-cols-2 gap-2">
            {[{ v: "Phone", I: Phone }, { v: "Email", I: Mail }].map(({ v, I }) => (
              <button key={v} type="button"
                onClick={() => dispatch({ type: "SET_FIELD", key: "candidateConnectVia", value: form.candidateConnectVia === v ? "" : v })}
                className={`flex items-center justify-center gap-2 rounded-xl border py-2.5 text-xs font-semibold transition-all ${
                  form.candidateConnectVia === v
                    ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                    : "border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50/50 hover:text-blue-600"
                }`}>
                <I className="h-3.5 w-3.5" />{v}
              </button>
            ))}
          </div>
        </Field>
      )}

      <SelectField label="Candidate Response" error={errors.benchStatus}>
        <select value={form.benchStatus} disabled={benchStatusLoading}
          onChange={(e) => dispatch({ type: "SET_FIELD", key: "benchStatus", value: e.target.value })}
          className={cls.select(errors.benchStatus, benchStatusLoading)}>
          <option value="">{benchStatusLoading ? "Loading…" : "Select Response"}</option>
          {visibleBenchStatuses.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
        {benchStatusError && <p className="mt-1 text-[11px] text-red-500">{benchStatusError}</p>}
      </SelectField>
    </Section>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN MODAL
══════════════════════════════════════════════════════════ */

const CandidateUpdateModal = ({
  isOpen,
  candidate,
  draft,
  onClose,
  onSave,
  onSaveDraft,
  onClearDraft,
  onToggleBench,
  isMovingToBench = false,
  isCandidateOnBench = false,
  moveToBenchError = "",
  mode = "update",
  primaryActionLabel,
  rdApiBaseUrl,
  enableEngagement = false,
}) => {
  /* ── Stable API base ── */
  const apiBase = useMemo(() => {
    const raw = String(rdApiBaseUrl ?? API_BASE).trim();
    const base = raw || API_BASE;
    return base.endsWith("/") ? base : `${base}/`;
  }, [rdApiBaseUrl]);

  const isCreate = mode === "create";
  const candidateId = String(candidate?.candidate_id ?? candidate?.candidateId ?? candidate?.id ?? "").trim();

  /* ── Default locale values (stable) ── */
  const defaultTZ = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone ?? "", []);
  const defaultCountry = useMemo(() => {
    const lang = typeof navigator !== "undefined" ? navigator.language : "en-US";
    const cc = lang.split("-")[1];
    if (cc === "US") return "United States";
    if (cc === "CA") return "Canada";
    return "";
  }, []);

  /* ── Form state via reducer ── */
  const [form, dispatch] = useReducer(formReducer, null, () =>
    buildForm(null, defaultTZ, defaultCountry)
  );

  const [errors, setErrors] = useState({});
  const [showSecEmail, setShowSecEmail] = useState(false);
  const [showSecPhone, setShowSecPhone] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleteDocError, setDeleteDocError] = useState("");
  const [isDeletingDoc, setIsDeletingDoc] = useState(false);
  const [showEngagementDialog, setShowEngagementDialog] = useState(false);
  const [showRefDialog, setShowRefDialog] = useState(false);
  const [createCandidateKind, setCreateCandidateKind] = useState("new");
  const [refTypeInput, setRefTypeInput] = useState("");
  const [refNameInput, setRefNameInput] = useState("");
  const [refSaving, setRefSaving] = useState(false);
  const [refSaveError, setRefSaveError] = useState("");

  const snapshotRef = useRef("");
  const initKeyRef = useRef("");
  const isDeleteBlocked = norm(deleteDocError).includes("deactivated");

  /* ── Fetch lists via unified hook ── */
  const workAuthUrl = isOpen ? `${apiBase}work_authorization` : null;
  const { list: workAuthOptions, loading: workAuthLoading, error: workAuthError } = useFetchList(
    workAuthUrl,
    (p) => (Array.isArray(p?.data) ? p.data : []).map((x) => ({
      id: String(x?.id ?? x?.work_authorization_id ?? x?.authorization_id ?? ""),
      name: String(x?.authorization_name ?? x?.work_authorization_name ?? x?.name ?? "").trim(),
    })),
  );

  const benchUrl = isOpen && enableEngagement ? `${apiBase}bench_status` : null;
  const { list: benchStatusOptions, loading: benchStatusLoading, error: benchStatusError } = useFetchList(
    benchUrl,
    (p) => (Array.isArray(p?.data) ? p.data : []).map((x) => ({
      id: String(x?.id ?? x?.bench_status_id ?? x?.status_id ?? ""),
      name: String(x?.status_name ?? x?.bench_status_name ?? x?.status ?? x?.name ?? "").trim(),
    })),
  );

  const refUrl = isOpen ? `${apiBase}get_ref_data` : null;
  const { list: refOptions, setList: setRefOptions } = useFetchList(
    refUrl,
    (p) => {
      const raw = Array.isArray(p?.data) ? p.data : Array.isArray(p) ? p : [];
      const seen = new Map();
      raw.forEach((e) => {
        if (!e) return;
        const id = e.id ?? e.ref_id ?? e.referrer_id ?? e.referred_by_id ?? e.employee_id ?? null;
        const name = String(e.ref_name ?? e.name ?? e.referrer_name ?? e.employee_name ?? "").trim();
        const type = String(e.ref_type ?? e.type ?? "").trim();
        if ((id || name) && !seen.has(String(id ?? name)))
          seen.set(String(id ?? name), { id: id ?? name, name, type });
      });
      return Array.from(seen.values()).sort((a, b) => a.name.localeCompare(b.name));
    },
  );

  /* ── Load uploaded documents (update mode only) ── */
  useEffect(() => {
    if (!isOpen || isCreate || !candidateId) return;
    const ctrl = new AbortController();
    (async () => {
      try {
        const r = await fetch(`${apiBase}uploaded_docs/${encodeURIComponent(candidateId)}`, { signal: ctrl.signal });
        if (!r.ok) return;
        const payload = await r.json().catch(() => null);
        if (String(payload?.status ?? "").toLowerCase() !== "success") return;
        const docs = Array.isArray(payload?.data) ? payload.data : [];
        let resumeFileName = "", resumeFileUrl = "", resumeFileDocId = "";
        const visaFileDocs = [];
        docs.forEach((doc) => {
          const type = String(doc?.document_type ?? "").toUpperCase();
          const docId = String(doc?.doc_id ?? doc?.document_id ?? doc?.id ?? "").trim();
          const url = normalizeDocumentUrl(doc?.file_url);
          const name = fileNameFromUrl(url);
          if (!name) return;
          if (!resumeFileName && type === "RESUME") {
            resumeFileName = name;
            resumeFileUrl = url;
            resumeFileDocId = docId;
          }
          if (type === "VISA") visaFileDocs.push({ id: docId, name, url });
        });
        dispatch({
          type: "SET_FIELDS",
          payload: {
            resumeFileName,
            resumeFileUrl,
            resumeFileDocId,
            visaFileDocs,
          },
        });
      } catch { /* silently ignore AbortError and 404 */ }
    })();
    return () => ctrl.abort();
  }, [apiBase, candidateId, isCreate, isOpen]);

  /* ── Init form when modal opens or candidate changes ── */
  useEffect(() => {
    if (!isOpen) { initKeyRef.current = ""; return; }
    const key = `${mode}:${candidateId || "new"}`;
    if (initKeyRef.current === key) return;
    const src = candidate ?? draft ?? null;
    const built = buildForm(src, defaultTZ, defaultCountry);
    dispatch({ type: "RESET", payload: built });
    snapshotRef.current = JSON.stringify(built);
    setErrors({});
    setShowSecEmail(Boolean(src?.secondaryEmailId ?? src?.candidate_email_secondary));
    setShowSecPhone(Boolean(built.secondaryPhoneNumber));
    initKeyRef.current = key;
  }, [isOpen, mode, candidateId, candidate, draft, defaultTZ, defaultCountry]);

  /* ── Location-derived values (pure computation, no effects needed) ── */
  const allCountries = useMemo(() => Country.getAllCountries().map((c) => ({
    id: c.isoCode, name: c.name, isoCode: c.isoCode, phonecode: c.phonecode,
    latitude: c.latitude, longitude: c.longitude, timezones: c.timezones ?? [],
  })), []);

  const selectedCountry = useMemo(() =>
    allCountries.find((c) => String(c.id) === String(form.countryId ?? "")) ??
    allCountries.find((c) => norm(c.name) === norm(form.country)) ?? null,
    [allCountries, form.country, form.countryId]);

  const allStates = useMemo(() =>
    selectedCountry ? State.getStatesOfCountry(selectedCountry.isoCode).map((s) => ({
      id: s.isoCode, name: s.name, isoCode: s.isoCode, latitude: s.latitude, longitude: s.longitude,
    })) : [],
    [selectedCountry]);

  const selectedState = useMemo(() =>
    allStates.find((s) => String(s.id) === String(form.stateId ?? "")) ??
    allStates.find((s) => norm(s.name) === norm(form.state)) ?? null,
    [allStates, form.state, form.stateId]);

  const allCities = useMemo(() => {
    if (!selectedCountry?.isoCode || !selectedState?.isoCode) return [];
    const seen = new Set();
    return (City.getCitiesOfState(selectedCountry.isoCode, selectedState.isoCode) ?? [])
      .filter((c) => { const k = norm(c?.name); if (!k || seen.has(k)) return false; seen.add(k); return true; })
      .map((c, i) => ({ id: `${selectedCountry.isoCode}-${selectedState.isoCode}-${i}`, name: c.name, latitude: c.latitude, longitude: c.longitude }));
  }, [selectedCountry?.isoCode, selectedState?.isoCode]);

  const selectedCity = useMemo(() =>
    allCities.find((c) => norm(c.name) === norm(form.city)) ?? null,
    [allCities, form.city]);

  /** Derived timezone — no effect needed, just computed each render */
  const derivedTZ = useMemo(() =>
    resolveTimezone(selectedCountry, selectedState, selectedCity) || defaultTZ,
    [selectedCountry, selectedState, selectedCity, defaultTZ]);

  /** Sync timezone into form when it changes */
  useEffect(() => {
    if (!isOpen || form.timeZonePrimary === derivedTZ) return;
    dispatch({ type: "SET_FIELD", key: "timeZonePrimary", value: derivedTZ });
  }, [isOpen, derivedTZ, form.timeZonePrimary]);

  /** Sync phone code into form when country changes */
  const derivedPhoneCode = useMemo(() => toPhoneCode(selectedCountry?.phonecode), [selectedCountry?.phonecode]);
  useEffect(() => {
    if (!isOpen) return;
    if (form.phoneCountryCode === derivedPhoneCode && form.secondaryPhoneCountryCode === derivedPhoneCode) return;
    dispatch({ type: "SET_FIELDS", payload: { phoneCountryCode: derivedPhoneCode, secondaryPhoneCountryCode: derivedPhoneCode } });
  }, [isOpen, derivedPhoneCode, form.phoneCountryCode, form.secondaryPhoneCountryCode]);

  /* ── Bench status filtering (derived, no effect) ── */
  const immediateBench = useMemo(() =>
    benchStatusOptions.filter((o) => norm(o.name).includes("immediately") && !norm(o.name).includes("not")),
    [benchStatusOptions]);

  const visibleBenchStatuses = useMemo(() => {
    const cv = norm(form.candidateConnect);
    if (cv === "yes") return immediateBench;
    if (cv === "no") return benchStatusOptions.filter((o) => !norm(o.name).includes("immediately") && !norm(o.name).includes("closure"));
    return benchStatusOptions;
  }, [benchStatusOptions, form.candidateConnect, immediateBench]);

  /** Auto-select "immediately available" when connect = Yes */
  useEffect(() => {
    if (!enableEngagement || !isOpen) return;
    if (norm(form.candidateConnect) !== "yes" || form.benchStatus) return;
    const opt = immediateBench[0];
    if (opt?.id) dispatch({ type: "SET_FIELD", key: "benchStatus", value: String(opt.id) });
  }, [enableEngagement, form.candidateConnect, form.benchStatus, immediateBench, isOpen]);

  /** Clear benchStatus if it falls outside the currently visible options */
  useEffect(() => {
    if (!enableEngagement || !isOpen || !form.benchStatus) return;
    if (!visibleBenchStatuses.some((o) => o.id === String(form.benchStatus)))
      dispatch({ type: "SET_FIELD", key: "benchStatus", value: "" });
  }, [enableEngagement, isOpen, form.benchStatus, visibleBenchStatuses]);

  /* ── Work auth options (include current value even if not in list) ── */
  const visibleWorkAuths = useMemo(() => {
    const id = String(form.workAuthorization ?? "").trim();
    const name = String(form.workAuthorizationName ?? "").trim();
    if (!id && !name) return workAuthOptions;
    const exists = workAuthOptions.some((o) => o.id === id || norm(o.name) === norm(name));
    if (exists) return workAuthOptions;
    return [{ id: id || name, name: name || id }, ...workAuthOptions];
  }, [form.workAuthorization, form.workAuthorizationName, workAuthOptions]);

  /* ── Validation ── */
  const validate = (f) => {
    const e = {};
    const required = isCreate && createCandidateKind === "nonBench"
      ? {
          name: "Full Name", title: "Job Title",
        }
      : {
          name: "Full Name", title: "Job Title", emailId: "Email",
          experience: "Experience", previousClient: "Previous Client",
          city: "City", state: "State", timeZonePrimary: "Time Zone",
          country: "Country", workMode: "Work Mode",
          expectedSalary: "Expected Salary", workAuthorization: "Work Authorization",
        };
    Object.entries(required).forEach(([k, lbl]) => {
      if (k === "workMode") { if (!f[k]?.length) e[k] = `${lbl} is required`; return; }
      if (!String(f[k] ?? "").trim()) e[k] = `${lbl} is required`;
    });
    if (f.emailId && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.emailId))
      e.emailId = "Enter a valid email address";
    return e;
  };

  /* ── Save engagement ── */
  const saveEngagement = useCallback(async (cid, values) => {
    const user = JSON.parse(localStorage.getItem("user") ?? "null");
    const bsName = benchStatusOptions.find((o) => o.id === String(values.benchStatus ?? ""))?.name ?? "";
    const body = new URLSearchParams({
      candidate_id: String(cid),
      candidate_connect: String(values.candidateConnect ?? ""),
      connected_via: String(values.candidateConnectVia ?? ""),
      bench_status_id: String(values.benchStatus ?? ""),
      candidate_response: String(values.candidateResponse ?? "") || bsName,
      candidate_comments: String(values.comments ?? values.candidateReason ?? ""),
      updated_by: String(user?.id ?? ""),
    });
    const res = await fetch(`${apiBase}setCandidateEngagement`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
      body: body.toString(),
    });
    if (!res.ok) throw new Error(`Engagement save failed (${res.status})`);
    const payload = await res.json().catch(() => null);
    if (payload?.success === false || (payload?.status && norm(payload.status) !== "success"))
      throw new Error(payload?.message ?? "Failed to save engagement.");
  }, [apiBase, benchStatusOptions]);

  const handleDeleteUploadedDoc = useCallback(async () => {
    const docId = String(deleteDialog?.id ?? "").trim();
    const deleteKind = String(deleteDialog?.kind ?? "").trim();
    const userId = JSON.parse(localStorage.getItem("user") ?? "null")?.id ?? null;

    if (!docId) { setDeleteDocError("Document ID is missing."); return; }
    if (!userId) { setDeleteDocError("User session is missing."); return; }

    setDeleteDocError("");
    setIsDeletingDoc(true);
    try {
      const body = new URLSearchParams({
        doc_id: docId,
        delete_user_id: String(userId),
      });
      const res = await fetch(`${apiBase}delete_docs`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: body.toString(),
      });
      const text = await res.text();
      let payload = null;
      try { payload = text ? JSON.parse(text) : null; } catch { payload = { message: text }; }

      if (!res.ok) throw new Error(payload?.message ?? `Delete failed (${res.status})`);
      if (payload?.success === false || (payload?.status && norm(payload.status) !== "success"))
        throw new Error(payload?.message ?? "Unable to delete document.");

      const nextForm = deleteKind === "resume"
        ? { ...form, resumeFileName: "", resumeFileUrl: "", resumeFileDocId: "" }
        : { ...form, visaFileDocs: (form.visaFileDocs ?? []).filter((doc) => String(doc?.id ?? "") !== docId) };

      dispatch({
        type: "SET_FIELDS",
        payload: deleteKind === "resume"
          ? { resumeFileName: "", resumeFileUrl: "", resumeFileDocId: "" }
          : { visaFileDocs: nextForm.visaFileDocs },
      });
      snapshotRef.current = JSON.stringify(nextForm);
      setDeleteDialog(null);
    } catch (e) {
      setDeleteDocError(e?.message ?? "Unable to delete document.");
    } finally {
      setIsDeletingDoc(false);
    }
  }, [apiBase, deleteDialog, form]);

  const handleSaveEngagement = async () => {
    if (!candidateId) return;
    setSubmitError(""); setIsSubmitting(true);
    try { await saveEngagement(candidateId, form); setShowEngagementDialog(false); }
    catch (e) { setSubmitError(e?.message ?? "Unable to save engagement."); }
    finally { setIsSubmitting(false); }
  };

  /* ── Main save ── */
  const handleSave = () => {
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const payload = {
      ...form,
      candidateType: createCandidateKind,
      candidate_added_source: isCreate && createCandidateKind === "nonBench" ? "Non_Bench" : form.benchStatus,
      phoneCountryCode: String(form.phoneCountryCode ?? "").trim(),
      phoneNumber: String(form.phoneNumber ?? "").trim(),
      secondaryPhoneNumber: showSecPhone ? String(form.secondaryPhoneNumber ?? "").trim() : "",
      secondaryPhoneCountryCode: showSecPhone && form.secondaryPhoneNumber ? String(form.secondaryPhoneCountryCode ?? "").trim() : "",
      created_by: JSON.parse(localStorage.getItem("user") ?? "null")?.id ?? null,
    };
    // Strip engagement-only and UI-only fields from the persistence payload
    ["candidateConnect","candidateConnectVia","candidateResponse",
     "candidateReason","visaFileDocs","resumeFileUrl","resumeFileDocId"].forEach((k) => delete payload[k]);
    if (!isCreate || createCandidateKind !== "nonBench") {
      delete payload.candidateType;
      delete payload.candidate_added_source;
    }
    if (isCreate && createCandidateKind === "nonBench") {
      [
        "country", "countryId", "state", "stateId", "city", "cityId",
        "timeZonePrimary", "phoneCountryCode", "secondaryPhoneCountryCode",
      ].forEach((k) => delete payload[k]);
    }

    (async () => {
      setSubmitError(""); setIsSubmitting(true);
      let ok = true;
      try {
        if (isCreate) {
          ok = (await onSave?.({ info: payload })) !== false;
        } else if (candidateId) {
          ok = (await onSave?.({ candidateId, info: payload })) !== false;
        } else {
          onSaveDraft?.(payload);
        }
      } catch (e) {
        ok = false;
        setSubmitError(e?.message ?? "Unable to save.");
      } finally { setIsSubmitting(false); }
      if (ok) { snapshotRef.current = JSON.stringify(form); onClose?.(); }
    })();
  };

  const isDirty = useMemo(() => JSON.stringify(form) !== snapshotRef.current, [form]);

  const handleClearDraft = () => {
    if (candidateId) return;
    onClearDraft?.();
    const blank = buildForm(null, defaultTZ, defaultCountry);
    dispatch({ type: "RESET", payload: blank });
    setErrors({}); setShowSecEmail(false); setShowSecPhone(false);
  };

  /* ── Add referral ── */
  const handleSaveRef = async () => {
    const type = refTypeInput.trim(), name = refNameInput.trim();
    if (!type || !name) { setRefSaveError("Type and name are required."); return; }
    setRefSaving(true); setRefSaveError("");
    try {
      const user = JSON.parse(localStorage.getItem("user") ?? "null");
      const res = await fetch(`${apiBase}add_ref_data`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ref_type: type, ref_name: name, created_by: user?.id ?? null }),
      });
      if (!res.ok) throw new Error(`Request failed with ${res.status}`);
      const result = await res.json();
      const newId = result?.data?.id ?? result?.id ?? result?.data?.ref_id ?? result?.ref_id ?? name;
      const created = { id: newId, name, type };
      setRefOptions((prev) => {
        const m = new Map(prev.map((o) => [String(o.id), o]));
        m.set(String(newId), created);
        return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
      });
      dispatch({ type: "SET_FIELDS", payload: { referredById: String(newId), referredBy: name } });
      setShowRefDialog(false); setRefTypeInput(""); setRefNameInput("");
    } catch (e) { setRefSaveError(e?.message ?? "Unable to save."); }
    finally { setRefSaving(false); }
  };

  if (!isOpen) return null;

  /* ── Helpers for location change handlers ── */
  const onCountryChange = (val) => {
    const match = allCountries.find((c) => norm(c.name) === norm(val));
    if (match) dispatch({ type: "SET_COUNTRY", country: match.name, countryId: match.id });
    else dispatch({ type: "SET_FIELDS", payload: { country: val, countryId: null, state: "", stateId: null, city: "", cityId: null } });
    setErrors((p) => ({ ...p, country: undefined, state: undefined, city: undefined }));
  };

  const onStateChange = (val) => {
    const match = allStates.find((s) => norm(s.name) === norm(val));
    if (match) dispatch({ type: "SET_STATE", stateName: match.name, stateId: match.id });
    else dispatch({ type: "SET_FIELDS", payload: { state: val, stateId: null, city: "", cityId: null } });
    setErrors((p) => ({ ...p, state: undefined, city: undefined }));
  };

  const onCityChange = (val) => {
    const match = allCities.find((c) => norm(c.name) === norm(val));
    dispatch({ type: "SET_CITY", city: match?.name ?? val, cityId: match?.id ?? null });
    setErrors((p) => ({ ...p, city: undefined }));
  };

  const set = (key) => (e) => dispatch({ type: "SET_FIELD", key, value: e.target.value });

  const renderCreateFields = () => (
    <Section
      icon={createCandidateKind === "nonBench" ? Users : Briefcase}
      dot={createCandidateKind === "nonBench" ? "text-emerald-500" : "text-violet-500"}
      label={createCandidateKind === "nonBench" ? "Add Non Bench Candidate" : "Add New Bench Candidate"}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Full Name" required error={errors.name}>
          <input type="text" placeholder="e.g. John Doe" value={form.name} onChange={set("name")} className={cls.input(errors.name)} />
        </Field>
        <Field label="Job Title" required error={errors.title}>
          <input type="text" placeholder="e.g. Senior React Developer" value={form.title} onChange={set("title")} className={cls.input(errors.title)} />
        </Field>
        <Field label="Email" error={errors.emailId}>
          <input type="email" placeholder="name@company.com" value={form.emailId} onChange={set("emailId")} className={cls.input(errors.emailId)} />
        </Field>
        <Field label="Referred By">
          <div className="flex gap-1.5">
            <div className="relative flex-1">
              <select value={form.referredById ?? ""}
                onChange={(e) => {
                  const id = e.target.value;
                  const ref = refOptions.find((o) => String(o.id) === id);
                  dispatch({ type: "SET_FIELDS", payload: { referredById: id, referredBy: ref?.name ?? "" } });
                }}
                className={`${cls.select()} pr-8`}>
                <option value="">Select</option>
                {refOptions.map((o) => <option key={o.id} value={o.id}>{o.type ? `${o.name} (${o.type})` : o.name}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            </div>
            <button type="button" onClick={() => { setShowRefDialog(true); setRefSaveError(""); }}
              className="flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-200 transition-all">
              <UserPlus className="h-3.5 w-3.5" />
            </button>
          </div>
        </Field>
      </div>
    </Section>
  );

  /* ══════════════════════════════════
     RENDER
  ══════════════════════════════════ */
  return createPortal(
    <div className="fixed inset-0 z-[999] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="relative w-full max-w-4xl max-h-[92vh] flex flex-col rounded-3xl bg-white shadow-2xl ring-1 ring-slate-200 overflow-hidden">

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between gap-4 border-b border-slate-100 bg-white px-6 py-4">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={`flex h-8 w-8 items-center justify-center rounded-xl shrink-0 ${isCreate ? "bg-blue-600" : "bg-slate-800"}`}>
              <UserPlus className="h-4 w-4 text-white" />
            </div>
            <div className="min-w-0">
              <h4 className="text-base font-bold text-slate-900 leading-tight">
                {isCreate ? "Add New  Candidate" : "Update Candidate"}
              </h4>
              <p className="text-xs text-slate-400 truncate max-w-xs mt-0.5">
                {isCreate ? "Fill in the candidate profile details below."
                  : (candidate?.name ?? candidate?.candidate_name)
                    ? `${candidate?.name ?? candidate?.candidate_name}${(candidate?.title ?? candidate?.job_title) ? ` · ${candidate?.title ?? candidate?.job_title}` : ""}`
                    : "Draft Submission"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {enableEngagement && !isCreate && candidateId && (
              <button type="button" onClick={() => setShowEngagementDialog(true)} disabled={isSubmitting}
                className="inline-flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:opacity-60 transition-all">
                <Users className="h-3.5 w-3.5" />Candidate Engagement
              </button>
            )}
            {!isCreate && candidateId && typeof onToggleBench === "function" && (
              <div className="flex flex-col items-end gap-1">
                <button type="button" onClick={onToggleBench} disabled={isMovingToBench || isSubmitting}
                  className={`inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-xs font-semibold transition-all disabled:opacity-60 ${
                    isCandidateOnBench
                      ? "border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                  }`}>
                  {isCandidateOnBench ? <X className="h-3.5 w-3.5" /> : <UserPlus className="h-3.5 w-3.5" />}
                  {isMovingToBench ? "Saving…" : isCandidateOnBench ? "Remove from Bench" : "Move to Bench"}
                </button>
                {moveToBenchError && <p className="text-[11px] font-medium text-rose-600">{moveToBenchError}</p>}
              </div>
            )}
            {isDirty && (
              <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-600">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />Unsaved
              </span>
            )}
            <button onClick={() => !isSubmitting && onClose?.()} disabled={isSubmitting}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/50">
          {isCreate && (
            <>
              <div className="inline-flex w-fit max-w-full rounded-full border border-slate-200 bg-slate-100 p-1">
                {[
                  ["new", "Add Bench Candidate"],
                  ["nonBench", "Add Non Bench Candidate"],
                ].map(([kind, label]) => {
                  const active = createCandidateKind === kind;
                  return (
                    <button key={kind} type="button" onClick={() => setCreateCandidateKind(kind)}
                      className={`h-8 rounded-full px-4 text-xs font-semibold transition-all ${
                        active
                          ? "bg-white text-blue-700 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}>
                      {label}
                    </button>
                  );
                })}
              </div>
              {createCandidateKind === "nonBench" && renderCreateFields()}
            </>
          )}
          {(!isCreate || createCandidateKind === "new") && <div className="grid gap-3 sm:grid-cols-2">

            {/* ── Column 1 ── */}
            <div className="flex flex-col gap-3">

              <Section icon={Briefcase} dot="text-violet-500" label="Candidate Details">
                <Field label="Full Name" required error={errors.name}>
                  <input type="text" placeholder="e.g. John Doe" value={form.name} onChange={set("name")} className={cls.input(errors.name)} />
                </Field>
                <Field label="Job Title" required error={errors.title}>
                  <input type="text" placeholder="e.g. Senior React Developer" value={form.title} onChange={set("title")} className={cls.input(errors.title)} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Previous Client" required error={errors.previousClient}>
                    <input type="text" placeholder="Client Name" value={form.previousClient} onChange={set("previousClient")} className={cls.input(errors.previousClient)} />
                  </Field>
                  <Field label="Experience" required error={errors.experience}>
                    <input type="text" list="exp-opts" placeholder="e.g. 6 Years" value={form.experience} onChange={set("experience")} className={cls.input(errors.experience)} />
                    <datalist id="exp-opts">
                      {Array.from({ length: 21 }, (_, i) => <option key={i} value={i === 1 ? "1 Year" : `${i} Years`} />)}
                      <option value="20+ Years" />
                    </datalist>
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Worked With VDart">
                    <select value={form.workedWithVdart} onChange={set("workedWithVdart")} className={cls.select()}>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </SelectField>
                  <Field label="Referred By">
                    <div className="flex gap-1.5">
                      <div className="relative flex-1">
                        <select value={form.referredById ?? ""}
                          onChange={(e) => {
                            const id = e.target.value;
                            const ref = refOptions.find((o) => String(o.id) === id);
                            dispatch({ type: "SET_FIELDS", payload: { referredById: id, referredBy: ref?.name ?? "" } });
                          }}
                          className={`${cls.select()} pr-8`}>
                          <option value="">Select</option>
                          {refOptions.map((o) => <option key={o.id} value={o.id}>{o.type ? `${o.name} (${o.type})` : o.name}</option>)}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      </div>
                      <button type="button" onClick={() => { setShowRefDialog(true); setRefSaveError(""); }}
                        className="flex items-center justify-center h-10 w-10 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-blue-600 hover:border-blue-200 transition-all">
                        <UserPlus className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </Field>
                </div>
              </Section>

              <Section icon={Phone} dot="text-sky-500" label="Contact Information">
                <Field label="Email Address" required error={errors.emailId}
                  action={!showSecEmail && (
                    <button type="button" onClick={() => setShowSecEmail(true)}
                      className="text-[11px] font-semibold text-blue-600 hover:underline">+ Add Secondary</button>
                  )}>
                  <input type="email" placeholder="name@company.com" value={form.emailId} onChange={set("emailId")} className={cls.input(errors.emailId)} />
                  {showSecEmail && (
                    <input type="email" placeholder="Secondary Email" value={form.secondaryEmailId} onChange={set("secondaryEmailId")} className={`mt-2 ${cls.input()}`} />
                  )}
                </Field>
                <Field label="Phone Number"
                  action={!showSecPhone && (
                    <button type="button" onClick={() => setShowSecPhone(true)}
                      className="text-[11px] font-semibold text-blue-600 hover:underline">+ Add Secondary</button>
                  )}>
                  <div className="flex gap-2">
                    <input type="text" value={form.phoneCountryCode ?? "+1"} readOnly className="w-24 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 outline-none cursor-not-allowed" />
                    <input type="tel" placeholder="Phone Number" value={form.phoneNumber} onChange={set("phoneNumber")} className={`flex-1 ${cls.input()}`} />
                  </div>
                  {showSecPhone && (
                    <div className="mt-2 flex gap-2">
                      <input type="text" value={form.secondaryPhoneCountryCode ?? "+1"} readOnly className="w-24 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-600 outline-none cursor-not-allowed" />
                      <input type="tel" placeholder="Secondary Phone" value={form.secondaryPhoneNumber} onChange={set("secondaryPhoneNumber")} className={`flex-1 ${cls.input()}`} />
                    </div>
                  )}
                </Field>
                <Field label="LinkedIn URL">
                  <input type="text" placeholder="e.g.linkedin.com/in/" value={form.linkedinUrl} onChange={set("linkedinUrl")} className={cls.input()} />
                </Field>
              </Section>

              <Section icon={MapPin} dot="text-rose-500" label="Location & Preferences">
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Country" required error={errors.country}>
                    <input type="text" list="cty-opts" placeholder="Type country" value={form.country}
                      onChange={(e) => onCountryChange(e.target.value)} className={cls.input(errors.country)} />
                    <datalist id="cty-opts">{allCountries.map((c) => <option key={c.id} value={c.name} />)}</datalist>
                  </Field>
                  <Field label="State" required error={errors.state}>
                    <input type="text" list="st-opts" placeholder={selectedCountry ? "Type state" : "Select country first"}
                      value={form.state} disabled={!selectedCountry}
                      onChange={(e) => onStateChange(e.target.value)} className={cls.input(errors.state)} />
                    <datalist id="st-opts">{allStates.map((s) => <option key={s.id} value={s.name} />)}</datalist>
                  </Field>
                  <Field label="City" required error={errors.city}>
                    <input type="text" list="ci-opts" placeholder={selectedState ? "Type city" : "Select state first"}
                      value={form.city} disabled={!selectedCountry || !selectedState}
                      onChange={(e) => onCityChange(e.target.value)} className={cls.input(errors.city)} />
                    <datalist id="ci-opts">{allCities.map((c) => <option key={c.id} value={c.name} />)}</datalist>
                  </Field>
                </div>
                <Field label="Time Zone" required error={errors.timeZonePrimary}>
                  <input type="text" value={form.timeZonePrimary} readOnly className={`${cls.input(errors.timeZonePrimary)} bg-slate-50 cursor-not-allowed`} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <SelectField label="Relocation">
                    <select value={form.relocation} onChange={set("relocation")} className={cls.select()}>
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                      <option value="Maybe">Maybe</option>
                    </select>
                  </SelectField>
                  <SelectField label="Work Authorization" required error={errors.workAuthorization}>
                    <select value={form.workAuthorization ?? form.workAuthorizationName ?? ""} disabled={workAuthLoading}
                      onChange={(e) => dispatch({ type: "SET_FIELDS", payload: { workAuthorization: e.target.value, workAuthorizationName: "" } })}
                      className={cls.select(errors.workAuthorization, workAuthLoading)}>
                      <option value="">{workAuthLoading ? "Loading…" : "Select"}</option>
                      {visibleWorkAuths.map((o) => <option key={`wa-${o.id}`} value={o.id}>{o.name}</option>)}
                    </select>
                    {workAuthError && <p className="mt-1 text-[11px] text-red-500">{workAuthError}</p>}
                  </SelectField>
                </div>
                <Field label="Work Mode" required error={errors.workMode}>
                  <div className="flex flex-wrap gap-2">
                    {["Onsite","Hybrid","Remote"].map((m) => {
                      const active = (form.workMode ?? []).includes(m);
                      return (
                        <button key={m} type="button" onClick={() => dispatch({ type: "TOGGLE_WORK_MODE", mode: m })}
                          className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all ${
                            active ? "border-blue-500 bg-blue-600 text-white shadow-sm shadow-blue-200"
                                   : "border-slate-200 bg-white text-slate-500 hover:border-blue-200 hover:bg-blue-50/50 hover:text-blue-600"
                          }`}>
                          {active && <CheckCircle2 className="h-3 w-3" />}{m}
                        </button>
                      );
                    })}
                  </div>
                </Field>
              </Section>

              <Section icon={DollarSign} dot="text-emerald-500" label="Compensation & Notes">
                <Field label="Expected Salary" required error={errors.expectedSalary}>
                  <div className={`flex items-center gap-2.5 rounded-xl border px-3.5 py-2.5 transition-all focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 ${errors.expectedSalary ? "border-red-300 bg-red-50/30" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                    <span className="text-xs font-bold text-slate-400 select-none">{form.country === "Canada" ? "CAD" : "USD"}</span>
                    <input type="text"  value={form.expectedSalary}
                      onChange={set("expectedSalary")}
                      className="flex-1 bg-transparent text-sm text-slate-800 placeholder-slate-400 outline-none" />
                  </div>
                </Field>
                <Field label="Comments">
                  <textarea placeholder="Add notes, submission details, or anything relevant…"
                    value={form.comments} rows={4} onChange={set("comments")}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm placeholder-slate-400 resize-none outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 hover:border-slate-300 transition-all" />
                </Field>
              </Section>
            </div>

            {/* ── Column 2 ── */}
            <div className="flex flex-col gap-3">

              <Section icon={Tag} dot="text-indigo-500" label="Skills"
                badge={`${parseSkills(form.primarySkills).length + parseSkills(form.secondarySkills).length} tags`}>
                <Field label="Primary Skills" hint="(Enter or comma to add)">
                  <SkillTagInput value={form.primarySkills}
                    onChange={(v) => dispatch({ type: "SET_FIELD", key: "primarySkills", value: v })}
                    placeholder="e.g. React, TypeScript, Node.js" color="blue" />
                </Field>
                <div className="border-t border-dashed border-slate-200" />
                <Field label="Secondary Skills" hint="(Enter or comma to add)">
                  <SkillTagInput value={form.secondarySkills}
                    onChange={(v) => dispatch({ type: "SET_FIELD", key: "secondarySkills", value: v })}
                    placeholder="e.g. AWS, Docker, PostgreSQL" color="emerald" />
                </Field>
              </Section>

              <Section icon={FileText} dot="text-slate-500" label="Documents">
                <Field label="Resume">
                  {form.resumeFileName && !(form.resumeFile instanceof File) && (
                    <div className="mb-2"><DocRow
                      name={form.resumeFileName}
                      url={form.resumeFileUrl}
                      onDelete={form.resumeFileDocId ? () => {
                        setDeleteDocError("");
                        setDeleteDialog({ id: form.resumeFileDocId, kind: "resume", name: form.resumeFileName });
                      } : undefined}
                      deleteDisabled={isDeletingDoc}
                    /></div>
                  )}
                  {form.resumeFile instanceof File && (
                    <div className="mb-2"><DocRow
                      name={form.resumeFile.name}
                      url={form.resumeFile}
                      isStaged
                      onRemove={() => dispatch({ type: "SET_FIELD", key: "resumeFile", value: null })}
                    /></div>
                  )}
                  <label className="group flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-3 cursor-pointer hover:border-blue-300 hover:bg-blue-50/40 transition-all">
                    <FileText className="h-5 w-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                    <span className="text-[11px] font-semibold text-slate-500 group-hover:text-blue-600">
                      {form.resumeFileName ? "Replace Resume" : "Upload PDF / DOC"}
                    </span>
                    <input type="file" accept=".pdf,.doc,.docx" className="sr-only"
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) dispatch({ type: "SET_FIELD", key: "resumeFile", value: f }); }} />
                  </label>
                </Field>

                <Field label="Visa Documents">
                  {form.visaFileDocs?.length > 0 && (
                    <div className="mb-2 space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Uploaded</p>
                      {form.visaFileDocs.map((doc, i) => <DocRow
                        key={doc.id || i}
                        name={doc.name}
                        url={doc.url}
                        onDelete={doc.id ? () => {
                          setDeleteDocError("");
                          setDeleteDialog({ id: doc.id, kind: "visa", name: doc.name });
                        } : undefined}
                        deleteDisabled={isDeletingDoc}
                      />)}
                    </div>
                  )}
                  {form.visaFiles?.length > 0 && (
                    <div className="mb-2 space-y-1.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-blue-400">Pending Upload</p>
                      {form.visaFiles.map((file, i) => (
                        <DocRow key={i} name={file.name} url={file} isStaged
                          onRemove={() => dispatch({ type: "REMOVE_VISA_FILE", index: i })} />
                      ))}
                    </div>
                  )}
                  <label className="group flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50/60 p-3 cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/40 transition-all">
                    <Plus className="h-4 w-4 text-slate-400 group-hover:text-emerald-500 transition-colors" />
                    <span className="text-[11px] font-semibold text-slate-500 group-hover:text-emerald-600">
                      {form.visaFiles?.length ? "Add More Visa Documents" : "Upload Visa Documents"}
                    </span>
                    <span className="text-[10px] text-slate-400">PDF, DOC, JPG, PNG · Multiple allowed</span>
                    <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" multiple className="sr-only"
                      onChange={(e) => {
                        const files = Array.from(e.target.files ?? []);
                        if (files.length) dispatch({ type: "ADD_VISA_FILES", files });
                        e.target.value = "";
                      }} />
                  </label>
                </Field>
              </Section>
            </div>
          </div>}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 border-t border-slate-100 bg-white px-6 py-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              {isDirty && <span className="text-xs font-medium text-amber-600"></span>}
              {submitError && (
                <p className="flex items-center gap-1.5 text-xs text-red-600">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />{submitError}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {!isCreate && (
                <button onClick={handleClearDraft} disabled={Boolean(candidateId) || isSubmitting}
                  title={candidateId ? "Clear Draft is only available for submission drafts." : "Clear the draft values."}
                  className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
                  Clear Draft
                </button>
              )}
              <button onClick={() => !isSubmitting && onClose?.()} disabled={isSubmitting}
                className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60 transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSubmitting}
                className="flex items-center gap-2 rounded-xl bg-blue-600 px-6 py-2 text-sm font-semibold text-white hover:bg-blue-700 active:bg-blue-800 disabled:opacity-60 transition-colors shadow-sm shadow-blue-600/25">
                {isSubmitting
                  ? <><span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />Saving…</>
                  : primaryActionLabel ?? (isCreate ? "Add Candidate" : "Save Changes")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Engagement Dialog */}
      {enableEngagement && showEngagementDialog && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowEngagementDialog(false)} />
          <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80 overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-blue-600" />
                <h5 className="text-sm font-bold text-slate-900">Candidate Engagement</h5>
              </div>
              <button type="button" onClick={() => setShowEngagementDialog(false)}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4">
              <EngagementSection form={form} dispatch={dispatch} errors={{}}
                benchStatusLoading={benchStatusLoading} benchStatusError={benchStatusError}
                visibleBenchStatuses={visibleBenchStatuses} />
            </div>
            <div className="flex gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3.5">
              <button type="button" onClick={() => setShowEngagementDialog(false)}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleSaveEngagement} disabled={isSubmitting}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {isSubmitting ? "Saving..." : "Save Engagement"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {/* Add Referral Dialog */}
      {showRefDialog && createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setShowRefDialog(false)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80 overflow-hidden">
            <div className="relative overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 px-5 py-4">
              <div className="absolute inset-0 opacity-20 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at 80% 0%, #3b82f6, transparent 70%)" }} />
              <div className="relative flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 ring-1 ring-white/20">
                    <UserPlus className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <h5 className="text-sm font-bold text-white">Add Referral Source</h5>
                    <p className="text-[11px] text-slate-400 mt-0.5">Create a new referred-by entry</p>
                  </div>
                </div>
                <button type="button" onClick={() => setShowRefDialog(false)}
                  className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-white/10 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="px-5 py-5 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Referral Type <span className="text-red-500">*</span></label>
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                  {["Employee","Vendor","Partner","Recruiter"].map((t) => (
                    <button key={t} type="button" onClick={() => setRefTypeInput(t)}
                      className={`rounded-lg border px-3 py-1.5 text-[11px] font-semibold transition-all ${refTypeInput === t ? "border-blue-500 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}>
                      {t}
                    </button>
                  ))}
                </div>
                <input type="text" placeholder="Or type a custom type…" value={refTypeInput}
                  onChange={(e) => setRefTypeInput(e.target.value)}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm placeholder-slate-400 focus:bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-2">Full Name <span className="text-red-500">*</span></label>
                <input type="text" placeholder="e.g. John Doe" value={refNameInput}
                  onChange={(e) => setRefNameInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveRef(); } }}
                  className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm placeholder-slate-400 focus:bg-white outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all" />
              </div>
              {refSaveError && (
                <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                  <p className="text-xs text-red-600">{refSaveError}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3.5">
              <button type="button" onClick={() => setShowRefDialog(false)}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">Cancel</button>
              <button type="button" onClick={handleSaveRef} disabled={refSaving || !refTypeInput.trim() || !refNameInput.trim()}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors">
                {refSaving
                  ? <><span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />Saving…</>
                  : <><UserPlus className="h-3.5 w-3.5" />Add Referral</>}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {deleteDialog && createPortal(
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => !isDeletingDoc && setDeleteDialog(null)} />
          <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80 overflow-hidden">
            <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
              <div className="flex items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                  <Trash2 className="h-4 w-4" />
                </div>
                <div>
                  <h5 className="text-sm font-bold text-slate-900">Delete Document</h5>
                  <p className="text-[11px] text-slate-500 mt-0.5">This action cannot be undone.</p>
                </div>
              </div>
              <button type="button" onClick={() => !isDeletingDoc && setDeleteDialog(null)}
                className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-600">
                Are you sure you want to delete <span className="font-semibold text-slate-900">{deleteDialog.name}</span>?
              </p>
              {deleteDocError && (
                <div className="flex items-start gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-2.5">
                  <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-red-500" />
                  <p className="text-xs text-red-600">{deleteDocError}</p>
                </div>
              )}
            </div>
            <div className="flex gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3.5">
              <button type="button" onClick={() => setDeleteDialog(null)} disabled={isDeletingDoc}
                className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors">
                Cancel
              </button>
              <button type="button" onClick={handleDeleteUploadedDoc} disabled={isDeletingDoc || isDeleteBlocked}
                className="flex-1 flex items-center justify-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {isDeletingDoc
                  ? <><span className="h-3.5 w-3.5 rounded-full border-2 border-white/30 border-t-white animate-spin" />Deleting...</>
                  : <><Trash2 className="h-3.5 w-3.5" />Delete</>}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>,
    document.body,
  );
};

export default CandidateUpdateModal;
